import { useEffect, useState } from 'react';
import { localeUI } from '../lib/format';
import { CalendarDays, Pencil } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { NoCargo } from './NoCargo';
import { useFallo } from '../lib/useFallo';

type LossInfo = { id: string; date_of_loss: string | null; type_of_loss: string | null; added_by: string | null; created_at: string };

/**
 * Espejo de `backend/src/lib/tiposDePerdida.ts`, que es quien manda (el frontend no importa del
 * backend). Estaba duplicada con "Wind & Hail" mientras el intake decía lo mismo, y esa
 * duplicación es como el campo terminó con cuatro grafías del mismo dato en prod:
 * "Wind, Hail" (163), "Hail, Wind" (36), "Wind & Hail" (8), "Wind, hail" (5).
 */
const TYPES = ['Wind', 'Hail', 'Wind and Hail', 'Water', 'Fire', 'Hurricane', 'Other'];

/**
 * Loss info del claim (date of loss + type of loss), estilo Adjuster Info: muestra el
 * actual, permite actualizarlo, y guarda historial con quién y cuándo. Cada cambio se
 * espeja al claim (para el resto de la app + el weather).
 */
export function ClaimLossInfo({ claimId, onChanged }: { claimId: string; onChanged?: () => void }) {
  const { t } = useI18n();
  const [history, setHistory] = useState<LossInfo[]>([]);
  const [current, setCurrent] = useState<{ date_of_loss: string | null; type_of_loss: string | null }>({ date_of_loss: null, type_of_loss: null });
  const [adding, setAdding] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const [date, setDate] = useState('');
  const [type, setType] = useState('');
  const [busy, setBusy] = useState(false);

  const { fallo, marcar, limpiar } = useFallo();
  const load = () => api.get<{ history: LossInfo[]; current: { date_of_loss: string | null; type_of_loss: string | null } }>(`/api/claims/${claimId}/loss-info`)
    .then((r) => { setHistory(r.history); setCurrent(r.current); }).then(limpiar).catch(marcar);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [claimId]);

  const fmt = (iso: string) => new Date(iso).toLocaleDateString(localeUI(), { month: 'short', day: 'numeric', year: 'numeric' });
  // date_of_loss puede venir como 'YYYY-MM-DD' o como ISO completo ('...T00:00:00.000Z').
  // Tomamos SOLO los primeros 10 (la fecha) y la fijamos a mediodía local para no correrla
  // por timezone; si aun así no parsea, mostramos '—' (evita el "Invalid Date").
  const fmtDate = (d: string | null) => {
    if (!d) return '—';
    const dt = new Date(`${String(d).slice(0, 10)}T12:00:00`);
    return Number.isNaN(dt.getTime()) ? '—' : dt.toLocaleDateString(localeUI());
  };

  // El <input type="date"> exige 'YYYY-MM-DD' → recortamos por si viene ISO completo.
  function openForm() { setDate((current.date_of_loss ?? '').slice(0, 10)); setType(current.type_of_loss ?? ''); setAdding(true); }
  async function save() {
    if (!date && !type) return;
    setBusy(true);
    try { await api.post(`/api/claims/${claimId}/loss-info`, { dateOfLoss: date || null, typeOfLoss: type || null }); setAdding(false); await load(); onChanged?.(); }
    catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); } finally { setBusy(false); }
  }

  return (
    <div className="card" style={{ display: 'grid', gap: 8 }}>
      {fallo && <NoCargo onRetry={load} compact />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span className="muted" style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', textTransform: 'uppercase', letterSpacing: 0.4 }}><CalendarDays size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('loss.title')}</span>
        {!adding && <button className="btn ghost sm" style={{ padding: '0 8px', fontSize: 'var(--fs-2xs)' }} onClick={openForm}><Pencil size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('common.edit')}</button>}
        {history.length > 0 && <button className="btn ghost sm" style={{ padding: '0 8px', fontSize: 'var(--fs-2xs)' }} onClick={() => setShowHist((v) => !v)}>{showHist ? '▾' : '▸'} {t('loss.history')} ({history.length})</button>}
      </div>

      {/* Actual */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 'var(--fs-sm)' }}>
        <div><span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('loss.dateOfLoss')}</span><div style={{ fontWeight: 'var(--fw-semibold)' }}>{fmtDate(current.date_of_loss)}</div></div>
        <div><span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('loss.typeOfLoss')}</span><div style={{ fontWeight: 'var(--fw-semibold)' }}>{current.type_of_loss || '—'}</div></div>
      </div>

      {adding && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', borderTop: '1px solid var(--line)', paddingTop: 8 }}>
          <input className="input sm" type="date" style={{ width: 160 }} value={date} onChange={(e) => setDate(e.target.value)} />
          <select className="input sm" style={{ width: 150 }} value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">{t('loss.typeOfLoss')}…</option>
            {TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <button className="btn sm" disabled={busy || (!date && !type)} onClick={save}>{t('common.save')}</button>
          <button className="btn ghost sm" onClick={() => setAdding(false)}>{t('common.cancel')}</button>
        </div>
      )}

      {showHist && history.length > 0 && (
        <div style={{ display: 'grid', gap: 6 }}>
          {history.map((h, i) => (
            <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 'var(--fs-xs)', background: 'var(--surface-2)', borderRadius: 8, padding: '6px 8px' }}>
              {i === 0 && history.length > 1 && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--green-soft)', color: 'var(--green)' }}>{t('loss.current')}</span>}
              <span>{fmtDate(h.date_of_loss)}</span>
              <span className="badge gray" style={{ fontSize: 'var(--fs-2xs)' }}>{h.type_of_loss || '—'}</span>
              <span style={{ flex: 1 }} />
              {h.added_by && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{h.added_by}</span>}
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{fmt(h.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
